if (!customElements.get("order-status")) {
  customElements.define(
    "order-status",
    class OrderStatus extends HTMLElement {
      static API_URL = 'https://assets.hardeck.de/api/ds/v1.0/order'
      static API_KEY = 'Ji6lHh3i9KGSg7wvwvSrATL4ZSR9jrVw'

      static TIMELINE_STATUS = {
        payment: 1,
        processing: 2,
        shipping: 3,
        delivered: 4,
        cancelled: 5,
        unknown: 6,
      };

      static STATUS_CODE_TO_TIMELINE_STATUS = {
        "ANZAHLUNG": OrderStatus.TIMELINE_STATUS.payment,
        "BEARBEITUNG": OrderStatus.TIMELINE_STATUS.processing,
        "VERSENDET": OrderStatus.TIMELINE_STATUS.shipping,
        "ZUGESTELLT": OrderStatus.TIMELINE_STATUS.delivered,
        "REKLA_BEARBEITUNG": OrderStatus.TIMELINE_STATUS.processing,
        "REKLA_VERSENDET": OrderStatus.TIMELINE_STATUS.shipping,
        "REKLA_ZUGESTELLT": OrderStatus.TIMELINE_STATUS.delivered,
        "STORNIERT": OrderStatus.TIMELINE_STATUS.cancelled,
        "UNBEKANNT": OrderStatus.TIMELINE_STATUS.unknown,
      }

      static TIMELINE_STATUS_TO_TITLE = {
        [OrderStatus.TIMELINE_STATUS.payment]: FoxTheme.orderStatusStrings.display.timeline.status.payment,
        [OrderStatus.TIMELINE_STATUS.processing]: FoxTheme.orderStatusStrings.display.timeline.status.processing,
        [OrderStatus.TIMELINE_STATUS.shipping]: FoxTheme.orderStatusStrings.display.timeline.status.shipping,
        [OrderStatus.TIMELINE_STATUS.delivered]: FoxTheme.orderStatusStrings.display.timeline.status.delivered,
        [OrderStatus.TIMELINE_STATUS.cancelled]: FoxTheme.orderStatusStrings.display.timeline.status.cancelled,
      }

      static TIMELINE_STATUS_TO_DELIVERY_DATE = {
        [OrderStatus.TIMELINE_STATUS.processing]: FoxTheme.orderStatusStrings.display.delivery_date.processing,
        [OrderStatus.TIMELINE_STATUS.shipping]: FoxTheme.orderStatusStrings.display.delivery_date.shipping,
        [OrderStatus.TIMELINE_STATUS.delivered]: FoxTheme.orderStatusStrings.display.delivery_date.shipping,
      }

      static APPOINTMENT_REQUIRED_STATES = [
        "LIEFERFAEHIG",
        "KUNDENLAGERUNG",
        "ANNAHMEVERZUG",
        "KD_NICHT_ANGETROFFEN",
        "TRANSPORT_NICHT_MOEGLICH",
        "REKLA_LIEFERFÄHIG",
        "REKLA_KUNDENLAGERUNG",
        "REKLA_ANNAHMEVERZUG",
        "REKLA_KD_NICHT_ANGETROFFEN",
        "REKLA_TRANSPORT_NICHT_MOEGLICH",
      ]

      static RETURN_POSITION_MARKER_POS_NUM = 500;
      static NORMAL_POSITION_POS_NUM_LIMIT = 99;

      constructor() {
        super();
      }

      connectedCallback() {
        this.form = this.querySelector("form");
        this.inputs = Array.from(this.form.querySelectorAll("input"));
        this.orderId = this.form.querySelector('input[name="order_id"]');
        this.pin = this.form.querySelector('input[name="pin"]');
        this.infobox = this.querySelector(".js-order-status-form__infobox");

        this.resultsContainer = this.querySelector(".js-order-status__results");
        this.resetButton = this.querySelector(".js-order-status__reset");
        this.template = this.querySelector("template");

        this.submitHandler = this.handleSubmit.bind(this);
        this.resetHandler = this.handleReset.bind(this);
        this.blurHandler = this.handleBlur.bind(this);

        this.form.addEventListener("submit", this.submitHandler);
        this.resetButton.addEventListener("click", this.resetHandler);

        this.orderId.addEventListener("blur", this.blurHandler);
        this.pin.addEventListener("blur", this.blurHandler);
      }

      disconnectedCallback() {
        this.form.removeEventListener("submit", this.submitHandler);
        this.resetButton.removeEventListener("click", this.resetHandler);

        this.orderId.removeEventListener("blur", this.blurHandler);
        this.pin.removeEventListener("blur", this.blurHandler);
      }

      handleSubmit(event) {
        event.preventDefault();

        const isValid = this.inputs.every((input) => input.checkValidity());

        if (!isValid) {
          this.inputs.forEach((input) => this.reportValidity(input));

          return;
        }

        const orderIdInput = this.form.querySelector('input[name="order_id"]');
        const pinInput = this.form.querySelector('input[name="pin"]');

        const orderId = orderIdInput.value.trim().toUpperCase();
        const pin = pinInput.value.trim();

        this.fetchOrderStatus(orderId, pin)
        .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error ${response.status}`);
            }

            return response.json();
          })
          .then((data) => {
            const clone = this.template?.content.cloneNode(true);

            this.initOrderStatusDisplay(clone, data);
            this.renderInitialTimeline(clone, data);
            this.renderReturnTimeline(clone, data);
            this.renderOrderData(clone, data);
            this.renderProductOverview(clone, data.positions);

            this.resultsContainer.innerHTML = "";
            this.resultsContainer.appendChild(clone);

            this.classList.add("order-status--results");
          })
          .catch((error) => {
            console.error('Failed to fetch order status:', error);

            this.infobox.classList.remove("hidden");
          });
      }

      reportValidity(input) {
        const isValid = input.checkValidity();

        input.classList.toggle("form-control--error", !isValid);
      }

      handleBlur(event) {
        const input = event.target;

        input.classList.remove("form-control--error");
      }

      handleReset() {
        this.form.reset();
        this.resultsContainer.innerHTML = "";
        this.classList.remove("order-status--results");
        this.infobox.classList.add("hidden");
      }

      fetchOrderStatus(orderId, pin) {
        return fetch(`${OrderStatus.API_URL}?orderid=${orderId}&pin=${pin}`, {
          headers: {
            'X-Api-Key': OrderStatus.API_KEY,
          }
        });
      }

      initOrderStatusDisplay(html, data) {
        const orderStatusDisplay = html.querySelector(".js-order-status-display");
        const isCancelled = data.progressCustomer === 'STORNIERT';
        const isReturn = data.positions.some((position) => this.isReturnMarker(position));

        orderStatusDisplay.classList.toggle("order-status-display--return", isReturn && !isCancelled);
      }

      // --- Timelines ---

      renderInitialTimeline(html, data) {
        if (data.progressCustomer === 'STORNIERT') {
          this.renderInitialTimelineCancelled(html, data);

          return;
        }

        if (data.positions.some((position) => this.isReturnMarker(position))) {
          this.renderInitialTimelineReturn(html, data);

          return;
        }

        const initialTimeline = html.querySelector(".js-order-status-display__initial-timeline");
        const content = [];

        const list = this.renderTimelineList(
          data.progressCustomerRegular,
          data.stateCustomerRegular,
          [
            OrderStatus.TIMELINE_STATUS.payment,
            OrderStatus.TIMELINE_STATUS.processing,
            OrderStatus.TIMELINE_STATUS.shipping,
            OrderStatus.TIMELINE_STATUS.delivered,
          ]
        );
        content.push(list);

        const status = this.renderTimelineStatus(data.progressCustomerRegular, data.stateCustomerRegular);
        content.push(status);

        const deliveryInfo = this.renderTimelineDeliveryDate(data.header.deliveryDate, data.header.deliveryDateState, data.progressCustomerRegular);

        initialTimeline.innerHTML = `${this.renderTimelineContent(content)}${deliveryInfo}`;
      }

      renderInitialTimelineReturn(html, data) {
        const initialTimeline = html.querySelector(".js-order-status-display__initial-timeline");
        const content = [];

        const header = this.renderTimelineHeader(FoxTheme.orderStatusStrings.display.timeline.header.initial);

        const status = this.renderTimelineStatus(
          data.progressCustomerRegular,
          data.stateCustomerRegular,
          'mobile'
        );
        content.push(status);

        initialTimeline.innerHTML = `${header}${this.renderTimelineContent(content)}`;
      }

      renderInitialTimelineCancelled(html, data) {
        const initialTimeline = html.querySelector(".js-order-status-display__initial-timeline");
        const content = [];


        const list = this.renderTimelineList(
          data.progressCustomerRegular,
          data.stateCustomerRegular,
          [
            OrderStatus.TIMELINE_STATUS.cancelled,
          ],
          'cancelled'
        );
        content.push(list);

        const status = this.renderTimelineStatus(
          data.progressCustomerRegular,
          data.stateCustomerRegular,
        );
        content.push(status);

        initialTimeline.innerHTML = this.renderTimelineContent(content);
      }

      renderReturnTimeline(html, data) {
        const isReturn = data.positions.some((position) => this.isReturnMarker(position));

        if (!isReturn) return;

        const returnTimeline = html.querySelector(".js-order-status-display__return-timeline");
        const content = [];

        const returnMarker = data.positions.find((position) => this.isReturnMarker(position));

        const header = this.renderTimelineHeader(
          FoxTheme.orderStatusStrings.display.timeline.header.return.title,
          FoxTheme.orderStatusStrings.display.timeline.header.return.description_html.replace('%date%', this.formatDeliveryDate(returnMarker.outgoingDate))
        );

        const list = this.renderTimelineList(
          data.progressCustomerClaim,
          data.stateCustomerClaim,
          [
            OrderStatus.TIMELINE_STATUS.processing,
            OrderStatus.TIMELINE_STATUS.shipping,
            OrderStatus.TIMELINE_STATUS.delivered
          ]
        );
        content.push(list);

        const status = this.renderTimelineStatus(
          data.progressCustomerClaim,
          data.stateCustomerClaim,
        );
        content.push(status);

        const deliveryInfo = this.renderTimelineDeliveryDate(data.header.deliveryDate, data.header.deliveryDateState, data.progressCustomerClaim);

        returnTimeline.innerHTML = `${header}${this.renderTimelineContent(content)}${deliveryInfo}`;
      }

      renderTimelineHeader(title, description = null) {
        return `
          <div class="order-status-display__timeline__header">
            <h2 class="order-status-display__timeline__header__title">${title}</h2>
            ${description ? `<p class="order-status-display__timeline__header__description">${description}</p>` : ''}
          </div>
        `
      }

      renderTimelineContent(content) {
        return `
          <div class="order-status-display__timeline__content">
            ${content.join('')}
          </div>
        `;
      }

      renderTimelineList(progress, state, statuses = [], modifier = null) {
        const currentStatus = OrderStatus.STATUS_CODE_TO_TIMELINE_STATUS[progress];

        const listItems = statuses.map((status, index) => {
          const isLast = index === statuses.length - 1;
          const isActive = status === currentStatus;
          const isLastStatusActive = statuses[statuses.length - 1] === currentStatus;
          const isDone = status < currentStatus || (isLast && isLastStatusActive);
          const currentModifier = modifier ?? (isDone ? 'done' : isActive ? 'active' : 'pending');

          const title = OrderStatus.TIMELINE_STATUS_TO_TITLE[status];
          const statusDescription = isActive
            ? FoxTheme.orderStatusStrings.display.timeline.description[this.formatStateKey(state)]
            : '';
          const deliveryDateLink = isActive ? this.renderBookDeliveryDateLink(state) : '';
          const description = `${statusDescription} ${deliveryDateLink}`;

          return this.renderTimelineListItem(currentModifier, title, description);
        }).join('');

        return `
          <ul class="order-status-display__timeline__list ${modifier ? `order-status-display__timeline__list--${modifier}` : ''}">
            ${listItems}
          </ul>
        `;
      }

      renderTimelineListItem(modifier, title, description) {
        return `
          <li class="order-status-display__timeline__list__item order-status-display__timeline__list__item--${modifier}">
            <p class="order-status-display__timeline__list__item__title">
              <span>${title}</span>
              ${modifier === 'done' ? FoxTheme.orderStatusStrings.display.icons.checkmark_large : ''}
              ${modifier === 'active' ? FoxTheme.orderStatusStrings.display.icons.hourglass_large : ''}
              ${modifier === 'cancelled' ? FoxTheme.orderStatusStrings.display.icons.cross_large : ''}
            </p>
            ${description ? `<p class="order-status-display__timeline__list__item__description">${description}</p>` : ''}
          </li>
        `;
      }

      renderTimelineDeliveryDate(deliveryDate, deliveryDateState, progress) {
        const currentStatus = OrderStatus.STATUS_CODE_TO_TIMELINE_STATUS[progress];
        const isValid = currentStatus === OrderStatus.TIMELINE_STATUS.processing || currentStatus === OrderStatus.TIMELINE_STATUS.shipping;

        const description = currentStatus === OrderStatus.TIMELINE_STATUS.delivered
          ? FoxTheme.orderStatusStrings.display.delivery_date.description.zugestellt
          : FoxTheme.orderStatusStrings.display.delivery_date.description[deliveryDateState.toLowerCase()] ?? FoxTheme.orderStatusStrings.display.delivery_date.description.unbekannt;

        return `
          <div class="order-status-display__timeline__delivery-date">
            <h2 class="order-status-display__timeline__delivery-date__title">${FoxTheme.orderStatusStrings.display.delivery_date.title}</h2>
            <p class="order-status-display__timeline__delivery-date__description">${description}</p>
            ${
              deliveryDate && isValid ? `<p class="order-status-display__delivery-date">
                ${FoxTheme.orderStatusStrings.display.icons.hardeck_delivery}
                <span>${OrderStatus.TIMELINE_STATUS_TO_DELIVERY_DATE[currentStatus]}: <b>${this.formatDeliveryDate(deliveryDate)}</b></span>
              </p>` : ''
            }
          </div>
        `;
      }

      renderTimelineStatus(progress, state, modifier = null) {
        const currentStatus = OrderStatus.STATUS_CODE_TO_TIMELINE_STATUS[progress];

        const title = OrderStatus.TIMELINE_STATUS_TO_TITLE[currentStatus];
        const description = FoxTheme.orderStatusStrings.display.timeline.description[this.formatStateKey(state)];
        const deliveryDateLink = this.renderBookDeliveryDateLink(state);

        return `
          <div class="order-status-display__timeline__status ${modifier ? `order-status-display__timeline__status--${modifier}` : ''}">
            <h2 class="order-status-display__timeline__status__title">${title}</h2>
            <p class="order-status-display__timeline__status__description">${description} ${deliveryDateLink}</p>
          </div>
        `;
      }

      renderBookDeliveryDateLink(state) {
        const isAppointmentRequired = OrderStatus.APPOINTMENT_REQUIRED_STATES.includes(state);

        if (!isAppointmentRequired) return '';

        return `
          <a href="/pages/liefertermin-buchen" target="_blank" rel="noopener noreferrer">
            ${FoxTheme.orderStatusStrings.display.timeline.header.book_delivery_date}
          </a>
        `;
      }

      // --- Order data ---

      renderOrderData(html, data) {
        const orderDataGrid = this.renderOrderDataGrid(data);

        const orderData = Array.from(
          html.querySelectorAll(".js-order-status-display__order-data")
        );

        orderData.forEach((element) => {
          element.innerHTML = orderDataGrid;
        });
      }

      renderOrderDataGrid(data) {
        const hasShippingAddress = data.shippingAddress.firstName !== ''
          && data.shippingAddress.lastName !== ''
          && data.shippingAddress.street !== ''
          && data.shippingAddress.postal !== ''
          && data.shippingAddress.city !== '';

        return `
          <div class="order-status-display__order-data">
            ${this.renderOrderDataItem(
              FoxTheme.orderStatusStrings.display.order_data.date,
              FoxTheme.utils.formatDateToString(data.header.orderDate, {
                day: "numeric",
                month: "long",
                year: "numeric",
              })
            )}
            ${this.renderOrderDataItem(
              FoxTheme.orderStatusStrings.display.order_data.sum,
              FoxTheme.utils.formatFloatPriceToString(data.header.totalAmountDiscount)
            )}
            ${this.renderOrderDataItem(
              FoxTheme.orderStatusStrings.display.order_data.customer_number,
              data.header.customerNumber,
              true
            )}
            ${this.renderOrderDataItem(
              FoxTheme.orderStatusStrings.display.order_data.billing_address,
              this.renderOrderDataAddress(data.billingAddress)
            )}
            ${this.renderOrderDataItem(
              FoxTheme.orderStatusStrings.display.order_data.shipping_address,
              this.renderOrderDataAddress(hasShippingAddress ? data.shippingAddress : data.billingAddress)
            )}
          </div>
        `;
      }

      renderOrderDataItem(title, value, fullwidth = false) {
        return `
          <div class="${
            fullwidth ? "order-status-display__order-data__item--fullwidth" : ""
          }">
            <h3 class="order-status-display__order-data__title">${title}</h3>
            <p class="order-status-display__order-data__value">${value.length > 0 ? value : '---'}</p>
          </div>
        `;
      }

      renderOrderDataAddress(address) {
        const result = `
          ${address.salutation ? address.salutation + '<br>' : ''}
          ${this.escapeHtml(address.firstName)} ${this.escapeHtml(address.lastName)}<br>
          ${this.escapeHtml(address.street)}<br>
          ${this.escapeHtml(address.postal)} ${this.escapeHtml(address.city)}
        `;

        return result;
      }

      // --- Product overview ---

      renderProductOverview(html, positions) {
        const displayPositions = this.getDisplayPositions(positions);
        const productOverviewList = this.renderProductOverviewList(displayPositions);

        const productOverview = Array.from(
          html.querySelectorAll(".js-order-status-display__product-overview")
        );

        productOverview.forEach((element) => {
          element.innerHTML = productOverviewList;
        });

        const productOverviewCount = Array.from(
          html.querySelectorAll(".js-order-status-display__product-overview__count")
        );

        productOverviewCount.forEach((element) => {
          element.innerHTML = displayPositions.length;
        });
      }

      renderProductOverviewList(positions) {
        return `
          <ul class="order-status-display__product-overview__list">
            ${positions.map((position, index) => this.renderProductOverviewListItem(position, index)).join("")}
          </ul>
        `;
      }

      renderProductOverviewListItem(position, index) {
        return `
          <li class="order-status-display__product-overview__item">
            <div class="order-status-display__product-overview__item__index hidden md:block">
              <b>${index + 1}</b>
            </div>
            <h3 class="order-status-display__product-overview__item__title"><b>${position.shortText ?? '---'}</b></h3>
            <div class="order-status-display__product-overview__item__sku">
              <span>${FoxTheme.orderStatusStrings.display.product_overview.sku}:</span>
              <span>${position.sku}</span>
            </div>
            <div class="order-status-display__product-overview__item__quantity">
              <div class="flex">
                <span>${FoxTheme.orderStatusStrings.display.product_overview.quantity}</span>
                <span class="md:hidden">:</span>
              </div>
              <div><b>${parseInt(position.count)}</b></div>
            </div>
            ${this.renderProductOverviewListBadge(position)}
            ${this.renderProductOverviewListDeliveryDate(position)}
          </li>
        `;
      }

      renderProductOverviewListBadge(position) {
        const label = FoxTheme.orderStatusStrings.display.product_overview.badge[position.customerState.toLowerCase()];

        return `
          <div class="order-status-display__product-overview__item__status">
            <span class="order-status-display__status-badge order-status-display__status-badge--${position.customerState.toLowerCase()}">
              ${label}
            </span>
          </div>
        `;
      }

      renderProductOverviewListDeliveryDate(position) {
        const currentStatus = OrderStatus.STATUS_CODE_TO_TIMELINE_STATUS[position.customerState];
        const isValid = currentStatus === OrderStatus.TIMELINE_STATUS.processing || currentStatus === OrderStatus.TIMELINE_STATUS.shipping || currentStatus === OrderStatus.TIMELINE_STATUS.delivered;

        if (!isValid) return '';
        if (!position.outgoingDate) return '';

        const formattedDeliveryDate = this.formatDeliveryDate(position.outgoingDate);

        return `
          <div class="order-status-display__product-overview__item__delivery-date">
            ${FoxTheme.orderStatusStrings.display.icons.hardeck_delivery}
            <span>${OrderStatus.TIMELINE_STATUS_TO_DELIVERY_DATE[currentStatus]}:</span>
            <span><b>${formattedDeliveryDate}</b></span>
          </div>
        `;
      }

      // --- Helpers ---

      isReturnMarker(position) {
        return Number(position.posNum) === OrderStatus.RETURN_POSITION_MARKER_POS_NUM;
      }

      isReturnPosition(position) {
        return Number(position.posNum) > OrderStatus.RETURN_POSITION_MARKER_POS_NUM;
      }

      isNormalPosition(position) {
        return Number(position.posNum) <= OrderStatus.NORMAL_POSITION_POS_NUM_LIMIT
          && Number(position.grossPrice) > 0;
      }

      getDisplayPositions(positions) {
        return positions.filter((position) => {
          if (this.isReturnMarker(position)) return false;
          if (this.isReturnPosition(position)) return true;

          return this.isNormalPosition(position);
        });
      }

      formatStateKey(state) {
        return state.toLowerCase().replace(/[äöüß]/g, m => ({'ä':'ae','ö':'oe','ü':'ue','ß':'ss'}[m]));
      }

      formatDeliveryDate(deliveryDate) {
        const weekPattern = /^\d{6}$/;
        const datePattern = /^\d{8}$/;

        if (weekPattern.test(deliveryDate)) {
          const week = parseInt(deliveryDate.slice(4), 10);

          if (Number.isNaN(week)) return '';

          return `${FoxTheme.orderStatusStrings.display.delivery_date.week} ${week}`;
        }

        if (datePattern.test(deliveryDate)) {
          const year = Number(deliveryDate.slice(0, 4));
          const month = Number(deliveryDate.slice(4, 6)) - 1;
          const day = Number(deliveryDate.slice(6, 8));

          const date = new Date(year, month, day);

          return FoxTheme.utils.formatDateToString(date, {
            day: "numeric",
            month: "short",
          });
        }

        return FoxTheme.utils.formatDateToString(deliveryDate, {
          day: "numeric",
          month: "short",
        });
      }

      escapeHtml(html) {
        const text = document.createTextNode(html);
        const div = document.createElement('div');

        div.appendChild(text);

        return div.innerHTML;
      }
    }
  );
}
